'use client'

import { useQuery } from '@tanstack/react-query'

import { Button } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import { useSharedTRPC } from '../../context'

export type NotificationFilter = 'ALL' | 'UNREAD' | 'SHARE' | 'ASSIGNMENT' | 'CONNECT' | 'PREMIUM'

interface NotificationFilterTabsProps {
  value: NotificationFilter
  onChange: (value: NotificationFilter) => void
  /** Which tabs this role can see. Order matters — it is the display order. */
  available: readonly NotificationFilter[]
  className?: string
}

const LABELS: Record<NotificationFilter, string> = {
  ALL: 'Semua',
  UNREAD: 'Belum dibaca',
  SHARE: 'Rekomendasi',
  ASSIGNMENT: 'Tugas dan Tantangan',
  CONNECT: 'Sambung sekolah-rumah',
  PREMIUM: 'Akses Premium',
}

/**
 * URL spelling of each tab, so `/pesan?tab=belum-dibaca` survives a reload, a shared
 * link, and the Back button.
 *
 * Lives here rather than in either app so a slug rename cannot leave one SPA behind,
 * and next to `LABELS` because the two must stay in step.
 */
export const NOTIFICATION_TAB_SLUGS = [
  'semua',
  'belum-dibaca',
  'rekomendasi',
  'tugas-dan-tantangan',
  'sambung-sekolah-rumah',
  'akses-premium',
] as const

export type NotificationTabSlug = (typeof NOTIFICATION_TAB_SLUGS)[number]

/**
 * Slugs a tab was renamed away from, mapped to the slug that replaced them.
 *
 * Links already sent and bookmarked still carry the old spelling. The routes accept these
 * so the link does not fall back to "Semua", then redirect to the current slug — the old
 * spelling is never written back into the URL.
 */
export const LEGACY_NOTIFICATION_TAB_SLUGS = {
  'guru-dan-kelas': 'tugas-dan-tantangan',
} as const satisfies Record<string, NotificationTabSlug>

export type LegacyNotificationTabSlug = keyof typeof LEGACY_NOTIFICATION_TAB_SLUGS

/** Every `?tab=` value a route should accept: the current slugs, then the retired ones. */
export const ACCEPTED_NOTIFICATION_TAB_SLUGS = [
  ...NOTIFICATION_TAB_SLUGS,
  ...(Object.keys(LEGACY_NOTIFICATION_TAB_SLUGS) as LegacyNotificationTabSlug[]),
] as [NotificationTabSlug | LegacyNotificationTabSlug, ...(NotificationTabSlug | LegacyNotificationTabSlug)[]]

/** The current spelling of a slug, or `undefined` when the slug is already current. */
export function renamedTabSlug(
  slug: NotificationTabSlug | LegacyNotificationTabSlug | undefined,
): NotificationTabSlug | undefined {
  return slug && slug in LEGACY_NOTIFICATION_TAB_SLUGS
    ? LEGACY_NOTIFICATION_TAB_SLUGS[slug as LegacyNotificationTabSlug]
    : undefined
}

const FILTER_BY_SLUG: Record<NotificationTabSlug, NotificationFilter> = {
  semua: 'ALL',
  'belum-dibaca': 'UNREAD',
  rekomendasi: 'SHARE',
  'tugas-dan-tantangan': 'ASSIGNMENT',
  'sambung-sekolah-rumah': 'CONNECT',
  'akses-premium': 'PREMIUM',
}

/** No param means the default tab, not a bad value — callers validate with `.catch(undefined)`. */
export function filterFromTabSlug(
  slug: NotificationTabSlug | LegacyNotificationTabSlug | undefined,
): NotificationFilter {
  if (!slug) return 'ALL'

  return FILTER_BY_SLUG[renamedTabSlug(slug) ?? (slug as NotificationTabSlug)]
}

/**
 * `ALL` maps to `undefined` on purpose: the default tab should leave no param behind, so
 * the canonical feed URL stays `/pesan`. Anything else that falls through to the same
 * `undefined` reads as "unrepresentable in the URL" rather than an error.
 */
export function tabSlugFromFilter(filter: NotificationFilter): NotificationTabSlug | undefined {
  switch (filter) {
    case 'UNREAD':
      return 'belum-dibaca'
    case 'SHARE':
      return 'rekomendasi'
    case 'ASSIGNMENT':
      return 'tugas-dan-tantangan'
    case 'CONNECT':
      return 'sambung-sekolah-rumah'
    case 'PREMIUM':
      return 'akses-premium'
    default:
      return undefined
  }
}

/**
 * Every category now has a tab. `ASSIGNMENT` was the last holdout — it went live with
 * `assignment.class_requested`, the teacher's "Hubungi admin" on the no-rombel blocker, and is
 * offered to the school app only: a family has no admin to wait on.
 *
 * A category the caller leaves out of `available` is still counted by the badge —
 * `notification.unreadCount` filters on audience and read state, not on category — so shipping a
 * producer without its tab leaves an admin a lit bell they can only clear under "Semua".
 */
export function NotificationFilterTabs({ value, onChange, available, className }: NotificationFilterTabsProps) {
  const trpc = useSharedTRPC()

  // Same `refetchOnWindowFocus` stand-in for realtime the bell badge uses, so a chip and the bell
  // light and clear together rather than one lagging the other by a navigation.
  const { data: unreadByCategory } = useQuery({
    ...trpc.notification.unreadCountByCategory.queryOptions(),
    refetchOnWindowFocus: true,
  })

  const totalUnread = Object.values(unreadByCategory ?? {}).reduce((sum, n) => sum + n, 0)

  /**
   * Whether a tab has anything unread behind it.
   *
   * `ALL` and `UNREAD` are not categories — they are views over every category — so both answer to
   * the total rather than to a lookup that would always miss.
   */
  const hasUnread = (filter: NotificationFilter): boolean => {
    if (filter === 'ALL' || filter === 'UNREAD') return totalUnread > 0

    return (unreadByCategory?.[filter] ?? 0) > 0
  }

  return (
    <nav className={cn('srd:flex srd:flex-wrap srd:gap-2', className)} aria-label='Filter pesan'>
      {available.map((filter) => (
        <Button
          key={filter}
          onClick={() => onChange(filter)}
          aria-pressed={value === filter}
          className={cn(
            typographyVariants({ variant: 'label-md' }),
            'srd:relative srd:rounded-full srd:px-4 srd:py-1 srd:border',
            value === filter
              ? 'srd:bg-blue-30 srd:border-blue-30 srd:text-navy-80'
              : 'srd:border-blue-70 srd:bg-transparent srd:text-neutral-white',
          )}
        >
          {LABELS[filter]}

          {/* A dot, not a count: the bell already carries the number, and six numbered chips read
              as a dashboard rather than as a filter row. Positioned on the border so it survives
              the chip's own background flipping when the tab is selected. */}
          {hasUnread(filter) && (
            <span
              aria-label='ada pesan belum dibaca'
              className='srd:bg-error srd:absolute srd:-top-0.5 srd:-right-0.5 srd:size-2.5 srd:rounded-full'
            >
              <span className='srd:bg-error srd:absolute srd:inline-flex srd:size-full srd:animate-ping srd:rounded-full srd:opacity-75' />
            </span>
          )}
        </Button>
      ))}
    </nav>
  )
}
